import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { MapPin, DollarSign, Briefcase, BookmarkX, ExternalLink } from "lucide-react";
import JobDetailsModal from "@/components/JobDetailsModal";

interface SavedJob {
  id: string;
  saved_at: string;
  notes: string | null;
  opportunity: {
    id: string;
    title: string;
    company_name: string;
    location: string;
    employment_type: string;
    description: string;
    salary_min: number;
    salary_max: number;
    disclosure_level_required: number;
    requirements: string | null;
    created_at: string;
  };
}

export default function SavedJobs() {
  const { toast } = useToast();
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  const fetchSavedJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("saved_jobs")
        .select(`
          *,
          opportunity:opportunities (
            id,
            title,
            company_name,
            location,
            employment_type,
            description,
            salary_min,
            salary_max,
            disclosure_level_required,
            requirements,
            created_at
          )
        `)
        .eq("candidate_id", user.id)
        .order("saved_at", { ascending: false });

      if (error) throw error;

      setSavedJobs((data as any) || []);
    } catch (error: any) {
      console.error("Fetch error:", error);
      toast({
        title: "Error Loading Saved Jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (savedJobId: string) => {
    try {
      const { error } = await supabase
        .from("saved_jobs")
        .delete()
        .eq("id", savedJobId);

      if (error) throw error;

      toast({
        title: "Job Removed",
        description: "Job removed from your saved list.",
      });

      fetchSavedJobs();
    } catch (error: any) {
      console.error("Unsave error:", error);
      toast({
        title: "Error Removing Job",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatSalary = (min: number, max: number) => {
    if (min && max) {
      return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
    }
    return "Competitive";
  };

  const getDisclosureBadge = (level: number) => {
    const labels = ["No Check", "Basic DBS", "Standard DBS", "Enhanced DBS"];
    const variants: ("secondary" | "outline" | "default")[] = ["secondary", "outline", "outline", "default"];
    return { label: labels[level] || "Unknown", variant: variants[level] || "secondary" };
  };

  const handleViewDetails = (job: SavedJob) => {
    setSelectedJob(job.opportunity);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Saved Jobs</CardTitle>
          <CardDescription>Jobs you've bookmarked for later</CardDescription>
        </CardHeader>
      </Card>

      {savedJobs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No saved jobs yet.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Bookmark jobs while browsing to save them for later!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {savedJobs.map((job) => {
            const disclosure = getDisclosureBadge(job.opportunity.disclosure_level_required);
            return (
              <Card key={job.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <CardTitle className="text-lg">{job.opportunity.title}</CardTitle>
                      <CardDescription className="text-base font-medium text-foreground">
                        {job.opportunity.company_name}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary" className="capitalize">
                      {job.opportunity.employment_type.replace("-", " ")}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {job.opportunity.description}
                  </p>

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{job.opportunity.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span>{formatSalary(job.opportunity.salary_min, job.opportunity.salary_max)}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <Badge variant={disclosure.variant as any}>
                      {disclosure.label}
                    </Badge>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(job)}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnsave(job.id)}
                      >
                        <BookmarkX className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <JobDetailsModal
        opportunity={selectedJob}
        open={modalOpen}
        onOpenChange={setModalOpen}
        isSaved={true}
        onSaveToggle={() => {
          fetchSavedJobs();
          setModalOpen(false);
        }}
        onApply={() => {
          setModalOpen(false);
        }}
      />
    </div>
  );
}
